import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CityViewModel } from '../models/CityViewModel';
import type { CountryViewModel } from '../models/CountryViewModel';
import type { CountryWithCodeViewModel } from '../models/CountryWithCodeViewModel';
import type { TownViewModel } from '../models/TownViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class LocationsService {
  /**
   * Countries
   * List countries
   * @returns CountryViewModel Success
   * @throws ApiError
   */
  public static async listCountries(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<CountryViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<CountryViewModel>>({
      method: 'GET',
      url: `/locations/countries`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cities
   * List cities by country code
   * @returns CityViewModel Success
   * @throws ApiError
   */
  public static async listCities(
    {
      countryCode,
    }: {
      countryCode: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<CityViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<CityViewModel>>({
      method: 'GET',
      url: `/locations/countries/${countryCode}/cities`,
      path: {
        ...(countryCode ? { countryCode: countryCode } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Towns
   * List towns by city id
   * @returns TownViewModel Success
   * @throws ApiError
   */
  public static async listTowns(
    {
      cityId,
    }: {
      cityId: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<TownViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<TownViewModel>>({
      method: 'GET',
      url: `/locations/cities/${cityId}/towns`,
      path: {
        ...(cityId ? { cityId: cityId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Countries (Code)
   * Gets countries with code
   * @returns CountryWithCodeViewModel Success
   * @throws ApiError
   */
  public static async listCountriesWithCode(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<CountryWithCodeViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<CountryWithCodeViewModel>>({
      method: 'GET',
      url: `/locations/countrycodes`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
